import React, { useState, useEffect } from "react";
import { Dialog, Classes, Icon } from "@blueprintjs/core";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import CommonTextField from "../CommonFields/CommonTextField";
import CommonLabel from "../CommonFields/CommonLabel";
import style from "./index.module.scss";
import { Modal } from "@mui/material";
import EmailPreview from "../EmailPreview/emailPreview";
import { ErrorToaster, SuccessToaster } from "../../utils/toaster";

// Text Components
const TemplateGreeting = ({ name }) => <p>Hello {name}:</p>;

const TemplateTimesheetInfo = ({ timesheetName, startDate, endDate }) => (
  <p>
    {timesheetName} - {startDate} - {endDate}
  </p>
);

const TemplateContractInfo = ({ contractName, contractId }) => (
  <p>
    {contractName} (ID: {contractId})
  </p>
);

const TemplateReminderText = ({ days }) => (
  <p>
    You have {days} days from the end of your timesheet period to create and
    submit the required timesheet/invoice for review and approval.
  </p>
);

const TemplateButton = ({ text }) => (
  <button className={style.createTimesheetButton}>
    {text || "CREATE & SUBMIT TIMESHEET"}
  </button>
);

const ActionButton = ({ text, className, onClick, disabled = false }) => (
  <button className={className} onClick={onClick} disabled={disabled}>
    {text}
  </button>
);

const EmailTemplates = ({
  getShowMailTemplate,
  templateType,
  reminderIndex,
  reminderData,
  reminderType,
  buttonText,
  onSave,
  templateBody,
  isCustomTemplate,
  templateData,
  toEmail,
  subject,
  templateName,
}) => {
  const [formData, setFormData] = useState({
    to: toEmail || "",
    cc: "",
    subject: subject || "",
    body: templateBody || "",
    isCustomTemplate: isCustomTemplate || false,
  });
  const [initialHtml, setInitialHtml] = useState("");

  const [isContentModified, setIsContentModified] = useState(false);
  const [ckeChange, setCkchange] = useState(false);
  const [editorInstance, setEditorInstance] = useState(null);

  // Initialize with template body if provided
  useEffect(() => {
    let initialBody = "";
    let isCustom = false;

    // Check if we have existing template data
    if (
      templateType === "reminder" &&
      reminderData?.reminderDetails?.[reminderIndex]
    ) {
      initialBody = reminderData.reminderDetails[reminderIndex].templateBody;
      isCustom =
        reminderData.reminderDetails[reminderIndex].customTemplate || false;
    } else if (templateType === "alert" && reminderData?.alertDetails) {
      initialBody = reminderData.alertDetails.templateBody;
      isCustom = reminderData.alertDetails.customTemplate || false;
    } else if (
      templateType === "expiration" &&
      reminderData?.expirationNotificationDetails
    ) {
      initialBody = reminderData.expirationNotificationDetails.templateBody;
      isCustom =
        reminderData.expirationNotificationDetails.customTemplate || false;
    } else if (
      templateType === "pastdue" &&
      reminderData?.pastDueNotificationDetails
    ) {
      initialBody = reminderData.pastDueNotificationDetails.templateBody;
      isCustom =
        reminderData.pastDueNotificationDetails.customTemplate || false;
    }

    // If we have a template body from props, use that
    if (templateBody) {
      initialBody = templateBody;
      isCustom = true;
    }

    // If we still don't have a body, use the default
    if (!initialBody || initialBody === "Default template") {
      initialBody = getDefaultBody();
      isCustom = false;
    }

    // If the body is empty string, keep it empty and mark as custom
    if (initialBody === "") {
      isCustom = true;
    }

    setFormData((prev) => ({
      ...prev,
      body: initialBody,
    }));

    setInitialHtml(initialBody);
    // Set the content modified flag based on whether it's a custom template
    setIsContentModified(isCustom);

    // If editor instance exists, update its content
    if (editorInstance) {
      editorInstance.setData(initialBody);
    }
  }, [templateBody, templateType, reminderIndex, reminderData, editorInstance]);

  const getOrdinalNumber = (num) => {
    const ordinals = [
      "First",
      "Second",
      "Third",
      "Fourth",
      "Fifth",
      "Sixth",
      "Seventh",
      "Eighth",
      "Ninth",
      "Tenth",
    ];
    return ordinals[num - 1] || `${num}th`;
  };

  // Get default subject based on template type
  function getDefaultSubject() {
    switch (reminderType) {
      case "TIMESHEET_CREATION_REMINDER":
        return "[Timesheet Name] - [Period]: Timesheet Creation Reminder";
      case "TIMESHEET_SUBMISSION_REMINDERS":
        return "[Timesheet Name] - [Period]: Submission Reminder";
      case "TIMESHEET_REVIEW_APPROVE_REMINDER":
        return "[Timesheet Name] - [Period]: Review & Approval Reminder";
      case "TIMESHEET_REJECTION_DISPUTE_REMINDER":
        return "[Timesheet Name] - [Period]: Rejection/Dispute Reminder";
      default:
        return "[Timesheet Name] - [Period]: Reminder";
    }
  }

  function getDefaultBody() {
    switch (reminderType) {
      case "TIMESHEET_CREATION_REMINDER":
        switch (templateType) {
          case "reminder":
            return "This is a friendly reminder that you can now create and submit your timesheet for the period indicated above. We recommend submitting it promptly to avoid any processing delays.";
          case "alert":
            return "This is a friendly reminder that you can now create and submit your timesheet for the period indicated above. We recommend submitting it promptly to avoid any processing delays.<br /> <br />After Timesheet Creation Period ends, you will not be able to create or submit Timesheet(s) for this period.";

          case "expiration":
            return "This is to inform you that the Timesheet Creation Period has expired. As a result, you will no longer be able to submit your Timesheet(s) for this period. <br /> <br /> Please contact your Supervisor or Department Head for assistance";
          default:
            return "";
        }

      case "TIMESHEET_SUBMISSION_REMINDERS":
        switch (templateType) {
          case "reminder":
            return "This is a friendly reminder that you can now submit your Timesheet for the period indicated above. We recommend submitting it promptly to avoid any processing delays.";
          case "alert":
            return "After the Timesheet Submission Period ends, you will not be able to submit Timesheet(s) for this period.";
          case "expiration":
            return "This is to inform you that the Timesheet Submission Period has expired. As a result, you will no longer be able to submit your Timesheet(s) for this period. <br /> <br />Please contact your Supervisor or Department Head for assistance";
          default:
            return "";
        }

      case "TIMESHEET_REVIEW_APPROVE_REMINDER":
        switch (templateType) {
          case "remainder":
            return "This is a friendly reminder that the above Timesheet/Invoice is assigned for you to review. Please complete this review to aviod any processing delays.";
          case "alert":
            return "This is a remainder to review this Timesheet. Please do so immediately to avoid processing delays.";
          case "expiration":
            return "This is impact downstream processing of this invoice leading to payment delays for the Service Provider. Please review this Timesheet ASAP.";
          default:
            return "";
        }

      default:
        return "";
    }
  }

  // Add formatReminderType function before getDialogTitle
  const formatReminderType = (type) => {
    if (!type) return "";
    return type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  function getDialogTitle() {
    if (templateType === "reminder") {
      return `Email Notification Template For ${getOrdinalNumber(
        reminderIndex + 1
      )}  ${formatReminderType(reminderType)}`;
    } else if (templateType === "alert") {
      return `Email Notification Template For ${formatReminderType(
        reminderType
      )} Expiration Alert`;
    } else if (templateType === "expiration") {
      return `Email Notification Template For ${formatReminderType(
        reminderType
      )} Period has expired`;
    } else {
      return `Email Notification Template For ${formatReminderType(
        reminderType
      )} Past Due Notification`;
    }
  }

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const [editorInitialized, setEditorInitialized] = useState(false);

  const [prevPopup, setPrevPopup] = useState(false);

  const stripHtmlTags = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const handleEditorChange = (event, editor) => {
    const newHtml = editor.getData();

    if (!editorInitialized) {
      setEditorInitialized(true);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      body: newHtml,
    }));

    const plainNew = stripHtmlTags(newHtml);
    const plainInitial = stripHtmlTags(initialHtml);
    const plainDefault = stripHtmlTags(getDefaultBody());

    const hasChanged = plainNew !== plainInitial;
    setCkchange(hasChanged);
    setIsContentModified(plainNew !== plainDefault);
  };

  const renderInput = (
    label,
    value,
    onChange,
    placeholder = "",
    isEmail = false
  ) => (
    <div className={style.inputContainer}>
      {isEmail && (
        <Icon
          icon="envelope"
          className={style.mailIcon}
          size={16}
          color="#464E5F"
        />
      )}
      <span className={style.inputLabel}>{label}</span>
      <CommonTextField
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={style.emailInput}
      />
    </div>
  );

  const editorConfiguration = {
    toolbar: {
      items: [
        "heading",
        "|",
        "fontSize",
        "fontFamily",
        "fontColor",
        "fontBackgroundColor",
        "|",
        "bold",
        "italic",
        "underline",
        "strikethrough",
        "subscript",
        "superscript",
        "|",
        "alignment",
        "indent",
        "outdent",
        "|",
        "link",
        "bulletedList",
        "numberedList",
        "todoList",
        "|",
        "blockQuote",
        "insertTable",
        "horizontalLine",
        "|",
        "removeFormat",
        "|",
        "undo",
        "redo",
      ],
      shouldNotGroupWhenFull: true,
    },
    fontSize: {
      options: [9, 11, 12, 13, 14, 16, 18, 20, 24, 28, 32, 36],
    },
    fontFamily: {
      options: [
        "default",
        "Arial, Helvetica, sans-serif",
        "Courier New, Courier, monospace",
        "Georgia, serif",
        "Lucida Sans Unicode, Lucida Grande, sans-serif",
        "Tahoma, Geneva, sans-serif",
        "Times New Roman, Times, serif",
        "Trebuchet MS, Helvetica, sans-serif",
        "Verdana, Geneva, sans-serif",
      ],
    },
    table: {
      contentToolbar: [
        "tableColumn",
        "tableRow",
        "mergeTableCells",
        "tableCellProperties",
        "tableProperties",
      ],
    },
    removePlugins: [
      "CKFinderUploadAdapter",
      "CKFinder",
      "EasyImage",
      "Image",
      "ImageCaption",
      "ImageStyle",
      "ImageToolbar",
      "ImageUpload",
      "MediaEmbed",
    ],
    ui: {
      viewportOffset: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
    },
    placeholder: "Type your email content here...",
    autoParagraph: false,
    removeEmptyTags: false,
  };

  const handleReset = () => {
    setCkchange(false);
    // Get the default body for this template type
    const defaultBody = getDefaultBody();

    setFormData((prev) => ({
      ...prev,
      body: defaultBody,
    }));
    setIsContentModified(false);

    // Reset editor content
    if (editorInstance) {
      editorInstance.setData(defaultBody);
    }
  };

  const handleSave = () => {
    // Get the default body for this template type
    const defaultBody = getDefaultBody();

    // Consider template as custom if it's different from default or empty
    const isCustom = formData.body !== defaultBody || formData.body === "";

    // Prepare data to save
    const saveData = {
      ...formData,
      customTemplate: isCustom,
      templateBody: formData.body, // Don't use || '' here, as we want to preserve empty strings
    };

    // Call the onSave callback with the data
    if (onSave) {
      onSave(saveData);
    }
    setCkchange(false);
    // Close the dialog
    getShowMailTemplate(false);
    SuccessToaster("Changes Updated Successfully");
  };

  const handlePreview = () => {
    // In a real app, you would show a preview of the email
    // console.log("Previewing email:", formData);F
    setPrevPopup(true);
  };

  return (
    <div>
      {!prevPopup && (
        <Dialog
          isOpen={true}
          onClose={() => getShowMailTemplate(false)}
          className={`${style.addManagerDialogBackground} ${style.mailTemplate}`}
        >
          <div className={`${Classes.DIALOG_BODY}`}>
            <div className={style.spaceBetween}>
              <h2 className={style.dialogTitle}>{getDialogTitle()}</h2>
              <Icon
                icon="cross"
                className={style.crossStyle}
                onClick={() => getShowMailTemplate(false)}
              />
            </div>

            <div className={style.formContainer}>
              {renderInput(
                "To :",
                formData.to,
                handleChange("to"),
                " name@email.com",
                true
              )}
              {renderInput(
                "Cc :",
                formData.cc,
                handleChange("cc"),
                " Select user to Cc...",
                true
              )}
              {renderInput(
                "Subject:",
                formData.subject,
                handleChange("subject"),
                " Subject"
              )}

              <div className={style.templateHeader}>
                <TemplateGreeting name={templateData.name} />
                <TemplateTimesheetInfo
                  timesheetName={templateData.timesheetName}
                  startDate={templateData.startDate}
                  endDate={templateData.endDate}
                />
                <TemplateContractInfo
                  contractName={templateData.contractName}
                  contractId={templateData.contractId}
                />
                <TemplateReminderText days={templateData.days} />
              </div>

              <div className={style.marginTop20}>
                <div className={style.editorContainer}>
                  <CKEditor
                    editor={ClassicEditor}
                    config={editorConfiguration}
                    data={formData.body}
                    onChange={handleEditorChange}
                    onReady={(editor) => {
                      setEditorInstance(editor);
                      editor.editing.view.change((writer) => {
                        writer.setStyle(
                          "height",
                          "150px",
                          editor.editing.view.document.getRoot()
                        );
                      });
                    }}
                  />
                </div>
              </div>

              <div className={style.marginTop20}>
                {templateType !== "expiration" && (
                  <TemplateButton text={buttonText} />
                )}
              </div>
            </div>
            <div className={style.buttonContainer}>
              <ActionButton
                text="RESET"
                className={style.resetButton}
                onClick={handleReset}
              />
              <div className={style.rightButtons}>
                <ActionButton
                  text="SAVE CHANGES"
                  className={style.saveButton}
                  onClick={handleSave}
                />
                <ActionButton
                  text="PREVIEW EMAIL"
                  className={style.previewButton}
                  onClick={handlePreview}
                  disabled={ckeChange}
                />
              </div>
            </div>
          </div>
        </Dialog>
      )}

      {prevPopup && (
        <EmailPreview
          bodyData={formData.body}
          templateType={templateName}
          prevPopup={prevPopup}
          setPrevPopup={setPrevPopup}
        />
      )}
    </div>
  );
};

export default EmailTemplates;
